import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from 'components/AppIcon';
import PrimaryTabNavigation from 'components/ui/PrimaryTabNavigation';
import MusicUpload from 'pages/creator-dashboard/components/MusicUpload';
import { useAuth } from '../../contexts/AuthContext';
import { getUserSubscription } from '../../services/billingService';
import { publishToGallery } from '../../services/computeWallet';


// Pipeline stage labels matching the 6-stage pipeline
const PIPELINE_STAGES = [
  { key: 'preflight',   label: 'Stage 1 — Music Analysis',         icon: 'Activity',   color: '#F43F5E' },
  { key: 'scenes',      label: 'Stage 2 — Scene Prompt Generation', icon: 'Layers',     color: '#8B5CF6' },
  { key: 'keyframes',   label: 'Stage 3 — Keyframe Image Generation', icon: 'Image',   color: '#06B6D4' },
  { key: 'animation',   label: 'Stage 4 — Scene Animation',         icon: 'Film',       color: '#10B981' },
  { key: 'assembly',    label: 'Stage 5 — Video Assembly',           icon: 'Clapperboard', color: '#F59E0B' },
  { key: 'audio',       label: 'Stage 6 — Audio Attachment',         icon: 'Music',      color: '#6366F1' },
];

const VIDEO_STYLES = [
  { id: 'cinematic-stock',      label: 'Cinematic',         icon: 'Film',        color: '#F59E0B' },
  { id: 'nature',               label: 'Nature',            icon: 'Trees',       color: '#10B981' },
  { id: 'urban',                label: 'Urban / City',      icon: 'Building2',   color: '#6366F1' },
  { id: 'worship',              label: 'Worship',           icon: 'Sun',         color: '#F59E0B' },
  { id: 'documentary',          label: 'Documentary',       icon: 'Video',       color: '#8B5CF6' },
  { id: 'particles',            label: 'Particles',         icon: 'Sparkles',    color: '#F43F5E' },
  { id: 'waves',                label: 'Abstract Waves',    icon: 'Activity',    color: '#8B5CF6' },
  { id: 'nebula',               label: 'Nebula',            icon: 'Circle',      color: '#06B6D4' },
  { id: 'geometric',            label: 'Geometric',         icon: 'Triangle',    color: '#10B981' },
  { id: 'realistic-photos',     label: 'Realistic Photos',  icon: 'Camera',      color: '#06B6D4' },
  { id: 'animated-lyrics',      label: 'Animated Lyrics',   icon: 'AlignCenter', color: '#EC4899' },
  { id: 'motion-typography',    label: 'Motion Typography', icon: 'Type',        color: '#6366F1' },
  { id: 'realistic-captions',   label: 'Realistic + Captions', icon: 'Subtitles', color: '#06B6D4' },
  { id: 'visualizer-cinematic', label: 'Visualizer + Cinematic', icon: 'Wand2',  color: '#8B5CF6' },
];

function PipelineStageIndicator({ activeStage, completedStages, isRunning }) {
  return (
    <div className="flex flex-col gap-2">
      {PIPELINE_STAGES?.map((stage, i) => {
        const isDone = completedStages?.includes(stage?.key);
        const isActive = activeStage === stage?.key && isRunning;
        return (
          <div
            key={stage?.key}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300"
            style={{
              background: isDone
                ? 'rgba(16,185,129,0.08)'
                : isActive
                ? `${stage?.color}12`
                : 'var(--color-muted)',
              border: `1px solid ${
                isDone ? 'rgba(16,185,129,0.25)' : isActive ? `${stage?.color}40` : 'var(--color-border)'
              }`,
            }}
          >
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                background: isDone
                  ? 'rgba(16,185,129,0.15)'
                  : isActive
                  ? `${stage?.color}20`
                  : 'var(--color-card)',
              }}
            >
              {isDone ? (
                <Icon name="CheckCircle2" size={14} color="var(--color-success)" />
              ) : isActive ? (
                <Icon name="Loader" size={14} color={stage?.color} className="animate-spin" />
              ) : (
                <Icon name={stage?.icon} size={14} color="var(--color-muted-foreground)" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="text-xs font-semibold"
                style={{
                  color: isDone
                    ? 'var(--color-success)'
                    : isActive
                    ? stage?.color
                    : 'var(--color-muted-foreground)',
                  fontFamily: 'var(--font-heading)',
                }}
              >
                {stage?.label}
              </p>
            </div>
            {isActive && (
              <div className="w-2 h-2 rounded-full animate-pulse flex-shrink-0" style={{ background: stage?.color }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function MusicToVideoPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, effectiveTier, isPlatformOwner } = useAuth();

  // Pre-fill from navigation state (e.g. from generated music)
  const prefilledMusic = location?.state?.musicFile || null;
  const prefilledStyle = location?.state?.visualStyle || 'cinematic-stock';

  const [userTier, setUserTier] = useState(effectiveTier || 'free');
  const [testMode, setTestMode] = useState(isPlatformOwner || false);

  // Music source state
  const [musicFile, setMusicFile] = useState(prefilledMusic);
  const [syncMode, setSyncMode] = useState('beats');
  const [outputType, setOutputType] = useState('full-video');
  const [lyricsText, setLyricsText] = useState('');

  // Video style
  const [visualStyle, setVisualStyle] = useState(prefilledStyle);
  const [showStyleDropdown, setShowStyleDropdown] = useState(false);
  const styleDropdownRef = useRef(null);

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeStage, setActiveStage] = useState(null);
  const [completedStages, setCompletedStages] = useState([]);
  const [genPercent, setGenPercent] = useState(0);
  const [genError, setGenError] = useState(null);

  // Result state
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [publishError, setPublishError] = useState(null);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // Load subscription tier
  useEffect(() => {
    if (!user?.id) return;
    if (isPlatformOwner) { setUserTier('enterprise'); setTestMode(true); return; }
    getUserSubscription(user?.id)?.then((sub) => {
      if (sub?.tier) setUserTier(sub?.tier);
    });
  }, [user?.id, isPlatformOwner]);

  // Close style dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (styleDropdownRef?.current && !styleDropdownRef?.current?.contains(e?.target)) {
        setShowStyleDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedStyle = VIDEO_STYLES?.find((s) => s?.id === visualStyle) || VIDEO_STYLES?.[0];

  // Called by MusicUpload → MusicVideoRenderer when video is ready
  const handleVideoGenerated = useCallback((result) => {
    setGeneratedVideo(result);
    setIsGenerating(false);
    setActiveStage(null);
    setCompletedStages(PIPELINE_STAGES?.map((s) => s?.key));
    setGenPercent(100);
    showToast('Music video generated successfully!', 'success');
  }, [showToast]);

  // Progress callback from MusicUpload
  const handleProgress = useCallback(({ stage, percent, stageIndex }) => {
    setIsGenerating(true);
    setGenPercent(percent || 0);
    // Map stage index to pipeline stage key
    const stageMap = [
      'preflight', 'preflight', 'preflight',
      'scenes', 'keyframes', 'animation',
      'assembly', 'audio', 'audio',
    ];
    const stageKey = stageMap?.[stageIndex] || 'preflight';
    setActiveStage(stageKey);
    // Mark previous stages as completed
    const stageKeys = PIPELINE_STAGES?.map((s) => s?.key);
    const currentIdx = stageKeys?.indexOf(stageKey);
    if (currentIdx > 0) {
      setCompletedStages(stageKeys?.slice(0, currentIdx));
    }
  }, []);

  const handleRenderError = useCallback((err) => {
    setIsGenerating(false);
    setActiveStage(null);
    setGenError(err || 'Generation failed. Please try again.');
    showToast(err || 'Generation failed', 'error');
  }, [showToast]);

  // Publish to community gallery
  const handlePublishToGallery = useCallback(async () => {
    if (!generatedVideo || !user?.id) return;
    setIsPublishing(true);
    setPublishError(null);
    try {
      await publishToGallery({
        userId: user?.id,
        title: generatedVideo?.title || 'My Music Video',
        generationType: 'video',
        prompt: generatedVideo?.prompt || '',
        thumbnailUrl: generatedVideo?.thumbnailUrl || null,
        videoUrl: generatedVideo?.videoUrl || null,
        audioUrl: generatedVideo?.audioUrl || null,
        allowRemix: true,
        metadata: {
          visualStyle: generatedVideo?.visualStyle || visualStyle,
          bpm: generatedVideo?.bpm,
          duration: generatedVideo?.duration,
          captionMode: generatedVideo?.captionMode,
          pipeline: 'music-to-video',
        },
      });
      setPublishSuccess(true);
      showToast('Published to Community Gallery!', 'success');
    } catch (err) {
      setPublishError(err?.message || 'Failed to publish. Please try again.');
      showToast('Failed to publish to gallery', 'error');
    }
    setIsPublishing(false);
  }, [generatedVideo, user?.id, visualStyle, showToast]);

  const handleDownload = useCallback(() => {
    const url = generatedVideo?.videoUrl || generatedVideo?.audioUrl;
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    const ext = generatedVideo?.mimeType?.includes('mp4') ? 'mp4' : 'webm';
    link.download = `${generatedVideo?.title || 'music-video'}-${generatedVideo?.videoId || Date.now()}.${ext}`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  }, [generatedVideo]);

  const handleReset = useCallback(() => {
    setGeneratedVideo(null);
    setCompletedStages([]);
    setActiveStage(null);
    setGenPercent(0);
    setGenError(null);
    setPublishSuccess(false);
    setPublishError(null);
    setIsGenerating(false);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryTabNavigation />

      {/* Toast */}
      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[300] px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fade-in-up"
          style={{
            background:
              toast?.type === 'error' ?'rgba(239,68,68,0.95)'
                : toast?.type === 'info' ?'rgba(99,102,241,0.95)' :'rgba(16,185,129,0.95)',
            color: 'white',
            backdropFilter: 'blur(8px)',
            boxShadow: 'var(--shadow-xl)',
          }}
        >
          <Icon
            name={toast?.type === 'error' ? 'AlertCircle' : toast?.type === 'info' ? 'Info' : 'CheckCircle2'}
            size={16}
            color="white"
          />
          <span className="text-sm font-medium" style={{ fontFamily: 'var(--font-body)' }}>
            {toast?.message}
          </span>
        </div>
      )}

      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">

          {/* Header */}
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <button
              onClick={() => navigate('/creator-dashboard')}
              className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:bg-[var(--color-muted)]"
              aria-label="Back to Creator Dashboard"
            >
              <Icon name="ArrowLeft" size={18} color="var(--color-foreground)" />
            </button>
            <div className="flex items-center gap-2.5">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #F43F5E, #8B5CF6)' }}
              >
                <Icon name="Music" size={20} color="white" />
              </div>
              <div>
                <h1
                  className="text-xl md:text-2xl font-bold"
                  style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}
                >
                  Music → Video
                </h1>
                <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                  Upload or select music · AI generates cinematic scenes · Keyframe animation pipeline
                </p>
              </div>
            </div>
            {testMode && (
              <span
                className="ml-auto text-xs px-2.5 py-1 rounded-full font-semibold"
                style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', fontFamily: 'var(--font-caption)' }}
              >
                TEST MODE
              </span>
            )}
          </div>

          {/* Pipeline Overview Banner */}
          <div
            className="mb-6 p-4 rounded-2xl"
            style={{ background: 'linear-gradient(135deg, rgba(244,63,94,0.06), rgba(139,92,246,0.06))', border: '1px solid rgba(244,63,94,0.2)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Zap" size={16} color="#F43F5E" />
              <span className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
                6-Stage AI Pipeline
              </span>
              <span
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(16,185,129,0.1)', color: 'var(--color-success)', fontFamily: 'var(--font-caption)' }}
              >
                Keyframe Architecture · ~$0.01–$0.02/video
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {PIPELINE_STAGES?.map((stage, i) => (
                <div
                  key={stage?.key}
                  className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl text-center"
                  style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: `${stage?.color}15` }}
                  >
                    <Icon name={stage?.icon} size={15} color={stage?.color} />
                  </div>
                  <p
                    className="text-xs font-semibold leading-tight"
                    style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}
                  >
                    {stage?.label?.split(' — ')?.[1] || stage?.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Main Layout */}
          <div className="flex flex-col lg:flex-row gap-6">

            {/* Left: Creation Controls */}
            <div className="w-full lg:w-[520px] xl:w-[560px] flex-shrink-0">
              <div
                className="rounded-2xl p-5 flex flex-col gap-5"
                style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-md)' }}
              >
                {/* Video Style Selector */}
                <div>
                  <p
                    className="text-sm font-semibold mb-2"
                    style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}
                  >
                    <Icon name="Palette" size={14} className="inline mr-1.5" color="#F43F5E" />
                    Video Style
                  </p>
                  <div className="relative" ref={styleDropdownRef}>
                    <button
                      onClick={() => setShowStyleDropdown((v) => !v)}
                      disabled={isGenerating}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all hover:opacity-90 disabled:opacity-50"
                      style={{
                        background: 'var(--color-card)',
                        borderColor: showStyleDropdown ? '#F43F5E' : 'var(--color-border)',
                      }}
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: `${selectedStyle?.color}20` }}
                      >
                        <Icon name={selectedStyle?.icon} size={15} color={selectedStyle?.color} />
                      </div>
                      <span
                        className="flex-1 text-sm font-semibold"
                        style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}
                      >
                        {selectedStyle?.label}
                      </span>
                      <Icon
                        name={showStyleDropdown ? 'ChevronUp' : 'ChevronDown'}
                        size={14}
                        color="var(--color-muted-foreground)"
                      />
                    </button>

                    {showStyleDropdown && (
                      <div
                        className="absolute top-full left-0 right-0 mt-1 rounded-xl overflow-hidden z-50 max-h-64 overflow-y-auto"
                        style={{
                          background: 'var(--color-card)',
                          border: '1px solid var(--color-border)',
                          boxShadow: 'var(--shadow-xl)',
                        }}
                      >
                        {VIDEO_STYLES?.map((style) => (
                          <button
                            key={style?.id}
                            onClick={() => {
                              setVisualStyle(style?.id);
                              setShowStyleDropdown(false);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all hover:bg-[var(--color-muted)]"
                            style={{
                              background: visualStyle === style?.id ? `${style?.color}10` : 'transparent',
                            }}
                          >
                            <div
                              className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ background: `${style?.color}20` }}
                            >
                              <Icon name={style?.icon} size={13} color={style?.color} />
                            </div>
                            <span
                              className="text-sm font-medium"
                              style={{
                                color:
                                  visualStyle === style?.id ? style?.color : 'var(--color-foreground)',
                                fontFamily: 'var(--font-body)',
                              }}
                            >
                              {style?.label}
                            </span>
                            {visualStyle === style?.id && (
                              <Icon name="CheckCircle2" size={13} color={style?.color} className="ml-auto" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Music Upload + Renderer */}
                <MusicUpload
                  musicFile={musicFile}
                  onMusicFileChange={setMusicFile}
                  syncMode={syncMode}
                  onSyncModeChange={setSyncMode}
                  outputType={outputType}
                  onOutputTypeChange={setOutputType}
                  lyricsText={lyricsText}
                  onLyricsTextChange={setLyricsText}
                  disabled={isGenerating}
                  testMode={testMode}
                  userTier={userTier}
                  onVideoGenerated={handleVideoGenerated}
                  onProgress={handleProgress}
                  onError={handleRenderError}
                />

                {/* Error state */}
                {genError && !isGenerating && (
                  <div
                    className="flex items-start gap-2 p-3 rounded-xl"
                    style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}
                  >
                    <Icon name="AlertCircle" size={16} color="#EF4444" className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-semibold" style={{ color: '#EF4444' }}>Generation failed</p>
                      <p
                        className="text-xs mt-0.5"
                        style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
                      >
                        {genError}
                      </p>
                    </div>
                    <button
                      onClick={() => setGenError(null)}
                      className="text-xs px-2 py-1 rounded-lg flex-shrink-0"
                      style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}
                    >
                      Dismiss
                    </button>
                  </div>
                )}

                {/* Legal notice */}
                <div
                  className="flex items-start gap-2 p-3 rounded-xl"
                  style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)' }}
                >
                  <Icon name="ShieldCheck" size={15} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                  <p
                    className="text-xs leading-relaxed"
                    style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
                  >
                    All AI-generated scenes are original. No copyrighted footage is replicated. Keyframe pipeline keeps GPU cost under $0.02 per video.
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Pipeline Status + Result */}
            <div className="flex-1 min-w-0 flex flex-col gap-6">

              {/* Pipeline Stage Tracker */}
              <div
                className="rounded-2xl p-5"
                style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-md)' }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="GitBranch" size={16} color="#F43F5E" />
                  <h2
                    className="text-sm font-bold"
                    style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}
                  >
                    Pipeline Status
                  </h2>
                  {isGenerating && (
                    <span
                      className="ml-auto text-xs px-2 py-0.5 rounded-full font-semibold animate-pulse"
                      style={{ background: 'rgba(244,63,94,0.15)', color: '#F43F5E', fontFamily: 'var(--font-caption)' }}
                    >
                      Running
                    </span>
                  )}
                  {genPercent === 100 && !isGenerating && (
                    <span
                      className="ml-auto text-xs px-2 py-0.5 rounded-full font-semibold"
                      style={{ background: 'rgba(16,185,129,0.15)', color: 'var(--color-success)', fontFamily: 'var(--font-caption)' }}
                    >
                      Complete
                    </span>
                  )}
                </div>

                {/* Overall progress bar */}
                {(isGenerating || genPercent > 0) && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                        Overall Progress
                      </span>
                      <span
                        className="text-xs font-bold"
                        style={{ color: '#F43F5E', fontFamily: 'var(--font-data)' }}
                      >
                        {Math.round(genPercent)}%
                      </span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-muted)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${genPercent}%`,
                          background: 'linear-gradient(90deg, #F43F5E, #8B5CF6)',
                        }}
                      />
                    </div>
                  </div>
                )}

                <PipelineStageIndicator
                  activeStage={activeStage}
                  completedStages={completedStages}
                  isRunning={isGenerating}
                />

                {/* Idle state hint */}
                {!isGenerating && genPercent === 0 && (
                  <div className="mt-4 flex items-center gap-2 p-3 rounded-xl" style={{ background: 'var(--color-muted)' }}>
                    <Icon name="Info" size={14} color="var(--color-muted-foreground)" />
                    <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                      Upload music on the left and click "Generate Music Video" to start the pipeline.
                    </p>
                  </div>
                )}
              </div>

              {/* Generated Video Result */}
              {generatedVideo && (
                <div
                  className="rounded-2xl p-5 animate-fade-in-up"
                  style={{ background: 'var(--color-card)', border: '1px solid rgba(16,185,129,0.3)', boxShadow: 'var(--shadow-md)' }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ background: 'rgba(16,185,129,0.15)' }}
                    >
                      <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                    </div>
                    <div>
                      <h2
                        className="text-sm font-bold"
                        style={{ color: 'var(--color-success)', fontFamily: 'var(--font-heading)' }}
                      >
                        Video Ready!
                      </h2>
                      <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                        {generatedVideo?.title || 'Music Video'}
                        {generatedVideo?.duration > 0 && ` · ${Math.round(generatedVideo?.duration)}s`}
                        {generatedVideo?.bpm && ` · ${generatedVideo?.bpm} BPM`}
                      </p>
                    </div>
                    <button
                      onClick={handleReset}
                      className="ml-auto flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all hover:opacity-80"
                      style={{
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-muted-foreground)',
                        background: 'var(--color-muted)',
                        fontFamily: 'var(--font-caption)',
                      }}
                    >
                      <Icon name="RefreshCw" size={12} color="var(--color-muted-foreground)" />
                      New Video
                    </button>
                  </div>

                  {/* Video preview */}
                  {generatedVideo?.videoUrl && generatedVideo?.videoUrl !== '#' && (
                    <div className="mb-4 rounded-xl overflow-hidden" style={{ background: '#000', aspectRatio: '16/9' }}>
                      <video
                        src={generatedVideo?.videoUrl}
                        controls
                        className="w-full h-full object-contain"
                        style={{ maxHeight: 280 }}
                      />
                    </div>
                  )}

                  {/* Metadata badges */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {generatedVideo?.visualStyle && (
                      <span
                        className="text-xs px-2 py-1 rounded-lg"
                        style={{ background: 'rgba(139,92,246,0.1)', color: '#8B5CF6', fontFamily: 'var(--font-caption)' }}
                      >
                        Style: {generatedVideo?.visualStyle}
                      </span>
                    )}
                    {generatedVideo?.bpm && (
                      <span
                        className="text-xs px-2 py-1 rounded-lg"
                        style={{ background: 'rgba(244,63,94,0.1)', color: '#F43F5E', fontFamily: 'var(--font-caption)' }}
                      >
                        {generatedVideo?.bpm} BPM
                      </span>
                    )}
                    {generatedVideo?.captionMode && generatedVideo?.captionMode !== 'none' && (
                      <span
                        className="text-xs px-2 py-1 rounded-lg"
                        style={{ background: 'rgba(16,185,129,0.1)', color: 'var(--color-success)', fontFamily: 'var(--font-caption)' }}
                      >
                        CC: {generatedVideo?.captionMode}
                      </span>
                    )}
                    <span
                      className="text-xs px-2 py-1 rounded-lg"
                      style={{ background: 'rgba(6,182,212,0.1)', color: '#06B6D4', fontFamily: 'var(--font-caption)' }}
                    >
                      Keyframe Pipeline
                    </span>
                  </div>

                  {/* Action buttons */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Download */}
                    <button
                      onClick={handleDownload}
                      className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
                      style={{
                        background: 'rgba(16,185,129,0.1)',
                        color: 'var(--color-success)',
                        border: '1px solid rgba(16,185,129,0.3)',
                      }}
                    >
                      <Icon name="Download" size={16} color="var(--color-success)" />
                      Download Video
                    </button>

                    {/* Full Preview */}
                    <button
                      onClick={() =>
                        navigate(`/music-video/${generatedVideo?.videoId || Date.now()}`, {
                          state: { item: { ...generatedVideo, mode: 'music-to-video' } },
                        })
                      }
                      className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
                      style={{
                        background: 'linear-gradient(135deg, rgba(244,63,94,0.1), rgba(139,92,246,0.1))',
                        color: '#F43F5E',
                        border: '1px solid rgba(244,63,94,0.3)',
                      }}
                    >
                      <Icon name="Play" size={16} color="#F43F5E" />
                      Full Preview
                    </button>

                    {/* Publish to Community Gallery */}
                    {publishSuccess ? (
                      <div
                        className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold"
                        style={{
                          background: 'rgba(16,185,129,0.1)',
                          color: 'var(--color-success)',
                          border: '1px solid rgba(16,185,129,0.3)',
                        }}
                      >
                        <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                        Published!
                      </div>
                    ) : (
                      <button
                        onClick={handlePublishToGallery}
                        disabled={isPublishing || !user?.id}
                        className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80 disabled:opacity-50"
                        style={{
                          background: 'rgba(99,102,241,0.1)',
                          color: 'var(--color-primary)',
                          border: '1px solid rgba(99,102,241,0.3)',
                        }}
                      >
                        {isPublishing ? (
                          <Icon name="Loader" size={16} color="var(--color-primary)" />
                        ) : (
                          <Icon name="Globe" size={16} color="var(--color-primary)" />
                        )}
                        {isPublishing ? 'Publishing...' : 'Publish to Gallery'}
                      </button>
                    )}
                  </div>

                  {publishError && (
                    <p
                      className="mt-2 text-xs text-center"
                      style={{ color: '#EF4444', fontFamily: 'var(--font-caption)' }}
                    >
                      {publishError}
                    </p>
                  )}

                  {publishSuccess && (
                    <button
                      onClick={() => navigate('/community-gallery')}
                      className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
                      style={{
                        background: 'var(--color-muted)',
                        color: 'var(--color-muted-foreground)',
                        border: '1px solid var(--color-border)',
                      }}
                    >
                      <Icon name="Globe" size={13} color="var(--color-muted-foreground)" />
                      View in Community Gallery
                    </button>
                  )}
                </div>
              )}

              {/* Cost Info Card */}
              <div
                className="rounded-2xl p-4"
                style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="DollarSign" size={15} color="#10B981" />
                  <h3
                    className="text-sm font-bold"
                    style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}
                  >
                    Cost Breakdown
                  </h3>
                </div>
                <div className="flex flex-col gap-2">
                  {[
                    { label: 'Music Analysis (Gemini Flash)', cost: '~$0.0001', icon: 'Activity', color: '#F43F5E' },
                    { label: 'Keyframe Images (max 8 × Stable Diffusion)', cost: '~$0.016 max', icon: 'Image', color: '#8B5CF6' },
                    { label: 'Frame Interpolation (no new AI frames)', cost: '~$0.004 max', icon: 'Film', color: '#06B6D4' },
                    { label: 'Video Assembly + Audio Attach', cost: '~$0.002', icon: 'Clapperboard', color: '#10B981' },
                  ]?.map((item) => (
                    <div key={item?.label} className="flex items-center gap-2">
                      <Icon name={item?.icon} size={12} color={item?.color} />
                      <span
                        className="flex-1 text-xs"
                        style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
                      >
                        {item?.label}
                      </span>
                      <span
                        className="text-xs font-bold"
                        style={{ color: item?.color, fontFamily: 'var(--font-data)' }}
                      >
                        {item?.cost}
                      </span>
                    </div>
                  ))}
                  <div
                    className="flex items-center gap-2 pt-2 mt-1"
                    style={{ borderTop: '1px solid var(--color-border)' }}
                  >
                    <Icon name="Zap" size={12} color="#F59E0B" />
                    <span
                      className="flex-1 text-xs font-semibold"
                      style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}
                    >
                      Total per video
                    </span>
                    <span
                      className="text-xs font-bold"
                      style={{ color: '#F59E0B', fontFamily: 'var(--font-data)' }}
                    >
                      ~$0.01–$0.02
                    </span>
                  </div>
                </div>
              </div>

              {/* Limits Card */}
              <div
                className="rounded-2xl p-4"
                style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Shield" size={15} color="#6366F1" />
                  <h3
                    className="text-sm font-bold"
                    style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}
                  >
                    Generation Limits
                  </h3>
                </div>
                <div className="flex flex-col gap-2">
                  {[
                    { label: 'Max scenes per video', value: '8', icon: 'Layers' },
                    { label: 'Max keyframes per video', value: '8', icon: 'Image' },
                    { label: 'Max video length (Free)', value: '30s', icon: 'Clock' },
                    { label: 'Max video length (Studio)', value: '3 min', icon: 'Clock' },
                    { label: 'Max video length (Pro Creator)', value: '5 min', icon: 'Clock' },
                    { label: 'Rate limit', value: '3/min · 30/hr', icon: 'Activity' },
                  ]?.map((item) => (
                    <div key={item?.label} className="flex items-center gap-2">
                      <Icon name={item?.icon} size={12} color="var(--color-muted-foreground)" />
                      <span
                        className="flex-1 text-xs"
                        style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
                      >
                        {item?.label}
                      </span>
                      <span
                        className="text-xs font-bold"
                        style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-data)' }}
                      >
                        {item?.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
